<template>
  <div>
    <div class="card">
      <div class="card-header py-2">
        <Spinner v-if="loading" margin_right="10px" height="20px" />
        <b-form-checkbox v-else class="display-inline marginright5" v-model="checkedAllPoints"
          :disabled="!loaded"></b-form-checkbox>
        <span @click="loaded ? (checkedAllPoints = !checkedAllPoints) : null">OpenStreetMap points</span>
        <div style="float: right;">
          <img @click="expanded = !expanded" style="height: 13px; cursor: pointer;"
            :src="expanded ? icons.chevronUp : icons.chevronDown" />
        </div>
      </div>
      <div class="card-body" v-if="expanded">
        <b-form-checkbox class="display-inline marginright5" v-model="active"></b-form-checkbox>
        <span @click="active = !active">Active</span>
        <b-form-checkbox class="display-inline marginright5" v-model="inactive"></b-form-checkbox>
        <span @click="inactive = !inactive">Inactive</span>
      </div>
    </div>
    <br>
    <div class="card">
      <div class="card-header py-2">
        <Spinner v-if="loading" margin_right="10px" height="20px" />
        <b-form-checkbox v-else class="display-inline marginright5" v-model="checkedAllCycleWays"
          :disabled="!loaded"></b-form-checkbox>
        <span @click="loaded ? (checkedAllCycleWays = !checkedAllCycleWays) : null">OpenStreetMap cycleways</span>
        <div style="float: right;">
          <img @click="expandedCyclewyas = !expandedCyclewyas" style="height: 13px; cursor: pointer;"
            :src="expandedCyclewyas ? icons.chevronUp : icons.chevronDown" />
        </div>
      </div>
      <div class="card-body" v-if="expandedCyclewyas">
        <b-form-checkbox class="display-inline marginright5" v-model="cyclewaysActive">
        </b-form-checkbox>
        <span @click="cyclewaysActive = !cyclewaysActive">Active</span>
        <b-form-checkbox class="display-inline marginright5" v-model="cyclewaysInactive">
        </b-form-checkbox>
        <span @click="cyclewaysInactive = !cyclewaysInactive">Inactive</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
:global(.osm-points-pointer-cursor),
:global(.osm-points-pointer-cursor *) {
  cursor: pointer !important;
}
</style>

<script lang="ts">
import BaseComponent from './Base.vue';
import { EventBus } from '@/ts/EventBus';
import { AlertStatus, DownloadStatus } from '@/ts/types';
import { defineComponent } from "vue";
import L from 'leaflet';
import { DataFilterExtension } from '@deck.gl/extensions';
import { DeckOverlay } from '@deck.gl-community/leaflet';
import { MapLasScatterplotLayer } from '@/ts/MapLasScatterplotLayer';
import { MapLasPathLayer } from '@/ts/MapLasPathLayer';

interface OpenStreetMapResponsePlace {
  id: number;
  fronttype: string;
  lat?: number | string;
  lon?: number | string;
  path?: any[];
  tags?: Record<string, string>;
  type: string;
  is_active: boolean;
}

interface OpenStreetMapBinaryData {
  length: number;
  attributes: {
    getPosition: { value: Float32Array; size: 2 };
    getFillColor: { value: Uint8Array; size: 4 };
    getLineColor: { value: Uint8Array; size: 4 };
    getFilterValue: { value: Float32Array; size: 1 };
  };
}

interface OpenStreetMapPathBinaryData {
  length: number;
  startIndices: Uint32Array;
  attributes: {
    getPath: { value: Float32Array; size: 2 };
    getFilterValue: { value: Float32Array; size: 1 };
  };
}

interface OpenStreetMapCardState {
  osmResult: OpenStreetMapResponsePlace[] | null;
  osmPointResults: OpenStreetMapResponsePlace[];
  osmPointLayer: MapLasScatterplotLayer | null;
  osmCyclewayLayer: MapLasPathLayer | null;
  osmCyclewayResults: OpenStreetMapResponsePlace[];
  osmCyclewaysProcessed: boolean;
  deckOverlay: DeckOverlay | null;
  mapMouseMoveHandler: ((event: L.LeafletMouseEvent) => void) | null;
  mapMouseOutHandler: (() => void) | null;
  mapClickHandler: ((event: L.LeafletMouseEvent) => void) | null;
  hoveredPointIndex: number | null;
}

function colorForType(type: string): [number, number, number] {
  if (type === 'parking') return [38, 120, 252];
  if (type === 'bicycle_parking') return [114, 128, 219];
  if (type === 'shelter') return [165, 42, 42];
  if (type === 'picnic_site') return [0, 128, 0];
  if (type === 'picnic_table') return [16, 194, 63];
  if (type === 'viewpoint') return [227, 64, 205];
  if (type === 'cycle_way') return [255, 0, 0];
  return [255, 165, 0];
}

function labelForType(type: string): string {
  if (type === 'parking') return 'Parking';
  if (type === 'shelter') return 'Wiata';
  if (type === 'bicycle_parking') return 'Parking rowerowy';
  if (type === 'picnic_site') return 'Piknik';
  if (type === 'picnic_table') return 'Stół';
  if (type === 'viewpoint') return 'Widok';
  if (type === 'cycle_way') return 'Rower';
  return 'Other';
}

declare module 'vue' {
  interface ComponentCustomProperties extends OpenStreetMapCardState { }
}

export default defineComponent({
  extends: BaseComponent,
  data() {
    return {
      checkedAllPoints: false,
      expanded: false,
      active: true,
      inactive: false,
      checkedAllCycleWays: false,
      expandedCyclewyas: false,
      cyclewaysActive: true,
      cyclewaysInactive: false,
      loading: false,
      loaded: false,
    };
  },
  created() {
    this.osmResult = null;
    this.osmPointResults = [];
    this.osmPointLayer = null;
    this.osmCyclewayLayer = null;
    this.osmCyclewayResults = [];
    this.osmCyclewaysProcessed = false;
    this.deckOverlay = null;
    this.mapMouseMoveHandler = null;
    this.mapMouseOutHandler = null;
    this.mapClickHandler = null;
    this.hoveredPointIndex = null;
    EventBus.$on('downloadExternalData', () => this.downloadPlacesOpenStreetMap());
    EventBus.$on('toggleOSMPoints', () => {
      if (this.osmResult) {
        this.checkedAllPoints = !this.checkedAllPoints;
      }
    });
    EventBus.$on('toggleOSMCycleways', () => {
      if (this.osmResult) {
        this.checkedAllCycleWays = !this.checkedAllCycleWays;
      }
    });
    EventBus.$on('selectedAreaChanged', () => this.onSelectedAreaChanged());
  },
  beforeUnmount() {
    if (window.mainmap && this.mapMouseMoveHandler) window.mainmap.off('mousemove', this.mapMouseMoveHandler);
    if (window.mainmap && this.mapMouseOutHandler) window.mainmap.off('mouseout', this.mapMouseOutHandler);
    if (window.mainmap && this.mapClickHandler) window.mainmap.off('click', this.mapClickHandler);
    if (window.mainmap) {
      window.mainmap.getContainer().classList.remove('osm-points-pointer-cursor');
      window.mainmap.closePopup();
      if (this.deckOverlay) window.mainmap.removeLayer(this.deckOverlay);
    }
  },
  methods: {

    isPointInSelectedArea(lat: number | string, lon: number | string) {
      const plat = Number(lat);
      const plon = Number(lon);
      if (!Number.isFinite(plat) || !Number.isFinite(plon)) return false;
      // @ts-ignore
      const areaKey = window.selectedArea;
      if (!areaKey) return true;
      // @ts-ignore
      const area = window.areas[areaKey];
      if (!area || !area[0] || !area[1]) return true;
      const minLat = Math.min(area[0][0], area[1][0]);
      const maxLat = Math.max(area[0][0], area[1][0]);
      const minLon = Math.min(area[0][1], area[1][1]);
      const maxLon = Math.max(area[0][1], area[1][1]);
      return (plat >= minLat && plat <= maxLat && plon >= minLon && plon <= maxLon);
    },

    isPathInSelectedArea(path: any[]) {
      if (!path || !path.length) return false;
      for (const p of path) {
        if (Array.isArray(p) && p.length >= 2) {
          if (this.isPointInSelectedArea(p[0], p[1]) || this.isPointInSelectedArea(p[1], p[0])) return true;
        } else if (p && typeof p === 'object') {
          const lat = p.lat ?? p.latitude ?? p[0];
          const lon = p.lon ?? p.lng ?? p.longitude ?? p[1];
          if (lat !== undefined && lon !== undefined && this.isPointInSelectedArea(lat, lon)) return true;
        }
      }
      return false;
    },

    downloadPlacesOpenStreetMap() {
      this.loading = true;
      const endPoint = window.apiHost + 'api/stringfield/openstreetmap/';
      this.downloadUrlWithCache(endPoint, 'openstreetmap', 'openstreetmap.txt', null, true).then((response) => {
        this.osmResult = response[1].results;
        this.loaded = true;
        if (response[0] == DownloadStatus.cache_fresh) {
          this.createAlert(AlertStatus.success, this.$t('placesLoaded', [response[1].results.length]).toString(), 2000);
        }
        if (response[0] == DownloadStatus.api_fresh) {
          this.createAlert(AlertStatus.success, this.$t('placesDownloaded', [response[1].results.length]).toString(), 2000);
        }
        if (response[0] == DownloadStatus.cache_last) {
          this.createAlert(AlertStatus.warning, this.$t('placesLoaded', [response[1].results.length]).toString(), 2000);
        }
      }).catch((error) => {
        this.createAlert(AlertStatus.danger, this.$t('placesError').toString(), 2000);
      }).finally(() => {
        this.loading = false;
      })
    },
    processPlacesOpenStreetMap() {
      if (!this.osmResult || !this.checkedAllPoints) return;
      this.createOsmPointLayer();
      this.updateOsmPointLayer();
    },
    processCyclewaysOpenStreetMap() {
      if (!this.osmResult) return;
      const cycleways: OpenStreetMapResponsePlace[] = [];
      for (const responsePlace of this.osmResult) {
        if (responsePlace.fronttype === 'node' || !responsePlace.path) continue;
        if (this.isPathInSelectedArea(responsePlace.path)) cycleways.push(responsePlace);
      }
      const positions: number[] = [];
      const startIndices: number[] = [];
      const filterValues: number[] = [];
      for (let lineIndex = 0; lineIndex < cycleways.length; lineIndex += 1) {
        const place = cycleways[lineIndex];
        startIndices.push(positions.length / 2);
        for (const point of place.path ?? []) {
          let lat: number;
          let lon: number;
          if (Array.isArray(point)) {
            // OSM paths are stored as [lat, lon].
            lat = Number(point[0]);
            lon = Number(point[1]);
          } else if (point && typeof point === 'object') {
            lat = Number(point.lat ?? point.latitude ?? point[0]);
            lon = Number(point.lon ?? point.lng ?? point.longitude ?? point[1]);
          } else {
            continue;
          }
          if (Number.isFinite(lon) && Number.isFinite(lat)) {
            positions.push(lon, lat);
            filterValues.push(place.is_active ? 1 : 0);
          }
        }
      }
      startIndices.push(positions.length / 2);
      const data: OpenStreetMapPathBinaryData = {
        length: cycleways.length,
        startIndices: Uint32Array.from(startIndices),
        attributes: {
          getPath: { value: Float32Array.from(positions), size: 2 },
          getFilterValue: { value: Float32Array.from(filterValues), size: 1 },
        },
      };
      this.osmCyclewayResults = cycleways;
      this.osmCyclewayLayer = new MapLasPathLayer({
        id: 'openstreetmap-cycleways',
        data,
        extensions: [new DataFilterExtension({ filterSize: 1 })],
        filterRange: this.getOsmCyclewayStatusFilterRange(),
        positionFormat: 'XY',
        getColor: [255, 0, 0, 255],
        getWidth: 5,
        widthUnits: 'pixels',
        visible: this.checkedAllCycleWays,
        pickable: false,
      });
      this.updateOsmDeckLayers();
      this.osmCyclewaysProcessed = true;
    },
    createOsmPointLayer() {
      if (!this.osmResult) return;
      const results = this.osmResult.filter((place) =>
        place.fronttype === 'node' && this.isPointInSelectedArea(place.lat ?? '', place.lon ?? '')
      );
      const positions = new Float32Array(results.length * 2);
      const fillColors = new Uint8Array(results.length * 4);
      const lineColors = new Uint8Array(results.length * 4);
      const filterValues = new Float32Array(results.length);
      results.forEach((place, index) => {
        const color = colorForType(place.type);
        positions[index * 2] = Number(place.lon);
        positions[index * 2 + 1] = Number(place.lat);
        fillColors.set([color[0], color[1], color[2], 51], index * 4);
        lineColors.set([color[0], color[1], color[2], 255], index * 4);
        filterValues[index] = place.is_active ? 1 : 0;
      });
      const data: OpenStreetMapBinaryData = {
        length: results.length,
        attributes: {
          getPosition: { value: positions, size: 2 },
          getFillColor: { value: fillColors, size: 4 },
          getLineColor: { value: lineColors, size: 4 },
          getFilterValue: { value: filterValues, size: 1 },
        },
      };
      this.osmPointResults = results;
      this.osmPointLayer = new MapLasScatterplotLayer({
        id: 'openstreetmap-points',
        data,
        extensions: [new DataFilterExtension({ filterSize: 1 })],
        filterRange: this.getOsmStatusFilterRange(),
        positionFormat: 'XY',
        _normalize: false,
        getRadius: 8,
        radiusUnits: 'pixels',
        stroked: true,
        getLineWidth: 2,
        lineWidthUnits: 'pixels',
        visible: this.checkedAllPoints,
        pickable: false,
      });
      this.ensureDeckOverlay();
    },
    ensureDeckOverlay() {
      if (!window.mainmap) return;
      if (!this.deckOverlay) {
        this.deckOverlay = new DeckOverlay({ layers: [] });
        window.mainmap.addLayer(this.deckOverlay);
      }
      if (!this.mapMouseMoveHandler) {
        this.mapMouseMoveHandler = (event: L.LeafletMouseEvent) => this.onMapMouseMove(event);
        window.mainmap.on('mousemove', this.mapMouseMoveHandler);
      }
      if (!this.mapMouseOutHandler) {
        this.mapMouseOutHandler = () => this.onMapMouseOut();
        window.mainmap.on('mouseout', this.mapMouseOutHandler);
      }
      if (!this.mapClickHandler) {
        this.mapClickHandler = (event: L.LeafletMouseEvent) => this.onMapClick(event);
        window.mainmap.on('click', this.mapClickHandler);
      }
    },
    onMapMouseMove(event: L.LeafletMouseEvent) {
      const { lat, lng } = event.latlng;
      const index = this.checkedAllPoints && this.osmPointLayer ? this.osmPointLayer.pick(lat, lng) : null;
      window.mainmap.getContainer().classList.toggle('osm-points-pointer-cursor', index !== null);
      this.hoveredPointIndex = index;
    },
    onMapMouseOut() {
      this.hoveredPointIndex = null;
      if (window.mainmap) window.mainmap.getContainer().classList.remove('osm-points-pointer-cursor');
    },
    onMapClick(event: L.LeafletMouseEvent) {
      if (!this.checkedAllPoints || !this.osmPointLayer) return;
      const index = this.osmPointLayer.pick(event.latlng.lat, event.latlng.lng);
      if (index === null) return;
      const place = this.osmPointResults[index];
      if (!place) return;
      const lat = Number(place.lat);
      const lon = Number(place.lon);
      const content = document.createElement('div');
      content.style.cssText = 'min-width: 180px; max-width: 260px; overflow-wrap: anywhere;';
      const title = document.createElement('b');
      title.textContent = labelForType(place.type);
      const coordinates = document.createElement('a');
      coordinates.href = `https://maps.google.com/maps?q=${lat},${lon}`;
      coordinates.target = '_blank';
      coordinates.rel = 'noopener noreferrer';
      coordinates.textContent = `${lat},${lon}`;
      content.append(title, document.createTextNode(' '), coordinates);
      if (window.isDesktop) {
        content.prepend(document.createTextNode(`${place.id} `));
        if (place.tags?.name) {
          const name = document.createElement('div');
          name.textContent = place.tags.name;
          content.append(name);
        }
      }
      window.mainmap.openPopup(content, [lat, lon]);
    },
    updateOsmPointLayer() {
      if (!this.osmPointLayer) return;
      this.ensureDeckOverlay();
      if (!this.deckOverlay) return;
      this.osmPointLayer.setVisible(this.checkedAllPoints);
      this.updateOsmDeckLayers();
    },
    updateOsmDeckLayers() {
      this.ensureDeckOverlay();
      if (!this.deckOverlay) return;
      const layers = [];
      if (this.osmPointLayer) {
        this.osmPointLayer.setVisible(this.checkedAllPoints);
        layers.push(this.osmPointLayer.getLayer());
      }
      if (this.osmCyclewayLayer) {
        this.osmCyclewayLayer.setVisible(this.checkedAllCycleWays);
        layers.push(this.osmCyclewayLayer.getLayer());
      }
      this.deckOverlay.setProps({ layers });
    },
    getOsmStatusFilterRange(): readonly [number, number] {
      if (this.active && this.inactive) return [0, 1];
      if (this.active) return [1, 1];
      if (this.inactive) return [0, 0];
      return [2, 2];
    },
    onCheckedAllChanged() {
      if (!this.osmResult) return;
      if (this.checkedAllPoints) {
        this.processPlacesOpenStreetMap();
      } else {
        this.onMapMouseOut();
        if (this.osmPointLayer) {
          this.osmPointLayer.setVisible(false);
          this.updateOsmPointLayer();
        }
      }
    },
    onOsmStatusFilterChanged() {
      if (!this.osmPointLayer) return;
      this.osmPointLayer.setFilterRange(this.getOsmStatusFilterRange());
      this.updateOsmPointLayer();
      if (this.hoveredPointIndex !== null) this.onMapMouseOut();
    },
    onSelectedAreaChanged() {
      if (!this.osmResult) return;
      this.osmCyclewayLayer = null;
      this.osmCyclewayResults = [];
      this.osmCyclewaysProcessed = false;
      if (this.checkedAllPoints) {
        this.processPlacesOpenStreetMap();
      } else {
        this.osmPointLayer = null;
        this.osmPointResults = [];
        this.updateOsmDeckLayers();
      }
      if (this.checkedAllCycleWays) this.onCheckedAllCyclewaysChanged();
    },
    onCheckedAllCyclewaysChanged() {
      if (!this.osmResult) return;
      if (this.checkedAllCycleWays && !this.osmCyclewaysProcessed) {
        this.processCyclewaysOpenStreetMap();
      }
      if (!this.osmCyclewayLayer) return;
      this.osmCyclewayLayer.setVisible(this.checkedAllCycleWays);
      this.osmCyclewayLayer.setFilterRange(this.getOsmCyclewayStatusFilterRange());
      this.updateOsmDeckLayers();
    },
    getOsmCyclewayStatusFilterRange(): readonly [number, number] {
      if (this.cyclewaysActive && this.cyclewaysInactive) return [0, 1];
      if (this.cyclewaysActive) return [1, 1];
      if (this.cyclewaysInactive) return [0, 0];
      return [2, 2];
    }
  },
  watch: {
    "checkedAllPoints": [{
      handler: "onCheckedAllChanged"
    }],
    "active": [{ handler: "onOsmStatusFilterChanged" }],
    "inactive": [{ handler: "onOsmStatusFilterChanged" }],
    "checkedAllCycleWays": [{
      handler: "onCheckedAllCyclewaysChanged"
    }],
    "cyclewaysActive": [{
      handler: "onCheckedAllCyclewaysChanged"
    }],
    "cyclewaysInactive": [{
      handler: "onCheckedAllCyclewaysChanged"
    }]
  }
})


</script>
